import { Button, useDisclosure } from "@heroui/react";
import { useEffect, useState } from "react";
import { CreateBillModal } from "@/app/components/modals/bill/createBillModal";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPencil, FaPlus } from "react-icons/fa6";
import { api } from "@/api";
import { Billing } from "@/api/billing";
import useErrorHandling from "@/hooks/useErrorHandling";
import { EditBillModal } from "@/app/components/modals/bill/editBillModal";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { getCurrentMonth, getMonthInRussian } from "@/utils";
import { PLAN_FEATURES } from "@/app/store/plans/constants";
import { NewMonthHandbook } from "@/app/components/handbooks/service/monthHandbook";
import { NewYearHandbook } from "@/app/components/handbooks/service/yearHandbook";
import { NewLocationHandbook } from "@/app/components/handbooks/locationHandbook";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useNavigate } from "react-router-dom";
import { useTour } from "@/hooks/useTour";

export function BillingPage() {
  const navigate = useNavigate();
  const [billings, setBillings] = useState<Billing[]>([]);
  const [billing, setBilling] = useState<Billing | null>(null);
  const firstLocation = useAppSelector(
    (state) => state.handbooks.location[0]?.id || 0,
  );

  const [currentYear, setCurrentYear] = useState<string>("2025");
  const [currentMonth, setCurrentMonth] = useState<string>(
    getMonthInRussian(getCurrentMonth()),
  );
  const [activeLocation, setActiveLocation] = useState<string>(
    firstLocation.toString(),
  );

  const handleError = useErrorHandling();

  const createBillingModalDisclosure = useDisclosure();
  const editBillingModalDisclosure = useDisclosure();

  const fullscreenDisclosure = useDisclosure();

  useTour(7);

  const editBilling = (billing: Billing) => {
    setBilling(billing);
    editBillingModalDisclosure.onOpen();
  };

  useEffect(() => {
    api.billings
      .list(currentMonth, Number(currentYear), Number(activeLocation))
      .then((response) => {
        setBillings(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    createBillingModalDisclosure.isOpen,
    editBillingModalDisclosure.isOpen,
    currentMonth,
    currentYear,
    activeLocation,
    handleError,
  ]);

  return (
    <>
      <CreateBillModal disclosure={createBillingModalDisclosure} />
      <EditBillModal
        disclosure={editBillingModalDisclosure}
        billing={billing}
      />
      <div className="flex flex-row flex-wrap gap-4 w-full">
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium" id="payments-title">
                Платежи
              </span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                color="primary"
                variant="flat"
                className="max-w-fit"
                onClick={() => navigate("/dashboard/payments/sent")}
              >
                Отправленные счета
              </Button>
              <Button
                startContent={<FaPlus />}
                color="primary"
                variant="solid"
                className="max-w-fit"
                onClick={() => createBillingModalDisclosure.onOpen()}
                id="create-bill-button"
              >
                Создать биллинг
              </Button>
            </div>
          </div>
          <div className="flex flex-row gap-2 mb-2">
            <NewLocationHandbook
              onSelectionChange={(key: any) => setActiveLocation(key)}
              selectedKey={activeLocation}
              children={<></>}
              isFinancial={false}
            />
          </div>
          <div className="flex flex-row gap-2 mb-2">
            <NewMonthHandbook
              onSelectionChange={(e) => {
                setCurrentMonth(e?.toString() || "Январь");
              }}
              selectedKey={currentMonth}
              children={<></>}
            />
            <NewYearHandbook
              onSelectionChange={(e) => {
                setCurrentYear(e?.toString() || "2024");
              }}
              selectedKey={currentYear}
              children={<></>}
            />
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <div id="payments-table">
              <TableBuilder
                displayOptions={false}
                removeWrapper={true}
                fullscreenDisclosure={fullscreenDisclosure}
                columns={[
                  {
                    label: "Номер",
                    key: "number",
                    type: ColumnType.Number,
                    sortable: true,
                  },
                  {
                    label: "Дата",
                    key: "date",
                    type: ColumnType.Date,
                    sortable: true,
                  },
                  {
                    label: "Плательщик",
                    key: "payer",
                    type: ColumnType.Renter,
                    sortable: true,
                    sortKey: "payer_display_name",
                    searchKey: "payer_display_name",
                  },
                  {
                    label: "Счёт",
                    key: "provider",
                    type: ColumnType.Requisite,
                    sortable: true,
                    sortKey: "provider_display_name",
                    searchKey: "provider_display_name",
                  },
                  {
                    label: "Сумма",
                    key: "amount",
                    type: ColumnType.Number,
                    sortable: true,
                  },
                  {
                    label: "Оплачено",
                    key: "paid",
                    type: ColumnType.Boolean,
                    sortable: true,
                  },
                  {
                    label: "Описание",
                    key: "description",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    key: "actions",
                    label: "Действия",
                    type: ColumnType.Actions,
                    actions: [
                      {
                        icon: <FaPencil />,
                        onClick: (_value, row) => {
                          editBilling(row);
                        },
                        planPermission: PLAN_FEATURES.BILLINGS,
                      },
                    ],
                  },
                ]}
                data={billings || []}
                rowsPerPage={10}
                defaultSortDescriptor={{
                  column: "number",
                  direction: "ascending",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
